import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { formatDate } from "../utils/dates";

/** How many rules `referrals.rules` holds, the same in every language. */
const RULE_COUNT = 7;

const STATUS_TONE = {
  signed_up: "bg-gray-100 text-gray-700",
  qualified: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  rejected: "bg-red-100 text-red-700",
};

/**
 * Invite a shop: the owner's link (copy, WhatsApp, a QR code to show on the
 * phone), the rules in plain words, and the shops it brought in with where
 * each one stands. Free months are given by the operator; nothing here asks.
 */
const Referrals = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [qr, setQr] = useState(null);

  useEffect(() => {
    shopsAPI
      .referrals()
      .then((response) => setData(response.data))
      .catch(() => setError(t("referrals.loadFailed")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The signup page lives on this same dashboard
  const link = data ? `${window.location.origin}${data.link_path}` : "";

  useEffect(() => {
    if (!link) return;
    QRCode.toDataURL(link, { margin: 1, width: 220 })
      .then(setQr)
      .catch(() => setQr(null));
  }, [link]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the link is on screen to copy by hand */
    }
  };

  if (error && !data) return <p className="text-red-600">{error}</p>;
  if (!data) return <p className="text-gray-500">…</p>;

  const share = `https://wa.me/?text=${encodeURIComponent(t("referrals.shareText", { link }))}`;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">🎟️ {t("referrals.title")}</h1>
        <p className="mt-1 text-gray-600">{t("referrals.subtitle")}</p>
      </div>

      <section className="bg-white rounded-xl shadow p-5">
        <h2 className="text-sm font-semibold text-gray-500">{t("referrals.yourLink")}</h2>
        <p className="mt-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 font-mono text-sm break-all" dir="ltr">
          {link}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={copy}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700"
          >
            {copied ? `✓ ${t("referrals.copied")}` : t("referrals.copy")}
          </button>
          <a
            href={share}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            {t("referrals.share")}
          </a>
        </div>
        {qr && <img src={qr} alt="" className="mt-4 w-44 h-44" />}
        {data.months_banked > 0 && (
          <p className="mt-3 text-sm font-medium text-emerald-700">
            {t("referrals.banked", { count: data.months_banked })}
          </p>
        )}
      </section>

      <section className="bg-white rounded-xl shadow p-5">
        <h2 className="text-lg font-semibold text-gray-800">{t("referrals.rulesTitle")}</h2>
        <ol className="mt-3 space-y-2 list-decimal ps-5 text-sm text-gray-700">
          {Array.from({ length: RULE_COUNT }, (_, index) => (
            <li key={index}>{t(`referrals.rules.${index}`)}</li>
          ))}
        </ol>
      </section>

      <section className="bg-white rounded-xl shadow p-5">
        <h2 className="text-lg font-semibold text-gray-800">{t("referrals.invitedTitle")}</h2>
        {data.referrals.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">{t("referrals.none")}</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-100">
            {data.referrals.map((referral) => (
              <li key={referral.id} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{referral.shop_name}</p>
                  <p className="text-xs text-gray-500">
                    {formatDate(referral.created_at)}
                    {referral.status === "signed_up" && !referral.they_paid && ` · ${t("referrals.waitingThem")}`}
                    {referral.status === "signed_up" && referral.they_paid && !referral.you_paid && ` · ${t("referrals.waitingYou")}`}
                  </p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_TONE[referral.status]}`}>
                  {t(`referrals.status.${referral.status}`)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default Referrals;
